import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import i18n from '@/i18n';

// jsPDF's built-in Helvetica has no Cyrillic glyphs, so Russian falls back to Uzbek in the PDF.
const pdfLng = () => (i18n.language === 'ru' ? 'uz' : i18n.language);
const tp = (key: string, opts: Record<string, unknown> = {}) => i18n.t(key, { ...opts, lng: pdfLng() });
const formatUZS = (amount: number) =>
  new Intl.NumberFormat(pdfLng() === 'en' ? 'en-US' : 'uz-UZ').format(amount) + ' ' + tp('misc_common.currency');

interface ContractPDFData {
  contract: {
    contract_number: string;
    total_amount: number;
    monthly_payment: number;
    term_months: number;
    created_at: string;
    status: string;
  };
  customer: {
    full_name: string;
    phone: string;
    passport?: string | null;
    pinfl?: string | null;
  } | null;
  /** T219: the schedule is the backend's (contract or limit check); the client never rebuilds it. */
  schedule: Array<{ n: number; due_date: string; amount: number; remaining: number }>;
  saleItems: Array<{
    product_name: string;
    quantity: number;
    unit_price: number;
    total: number;
    unit: string;
    mhik: string;
  }>;
}

export function generateContractPDF(data: ContractPDFData) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 20;

  // Title
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(tp('misc_pdf.title'), pageWidth / 2, y, { align: 'center' });
  y += 8;

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(data.contract.contract_number || tp('misc_pdf.contract_id_pending'), pageWidth / 2, y, { align: 'center' });
  y += 5;
  doc.setFontSize(9);
  doc.text(tp('misc_pdf.date', { date: new Date(data.contract.created_at).toLocaleDateString(pdfLng() === 'en' ? 'en-US' : 'uz') }), pageWidth / 2, y, { align: 'center' });
  y += 12;

  // Customer info
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(tp('misc_pdf.customer_info'), 14, y);
  y += 6;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  const customerLines = [
    tp('misc_pdf.name', { v: data.customer?.full_name || '—' }),
    tp('misc_pdf.phone', { v: data.customer?.phone || '—' }),
    tp('misc_pdf.passport', { v: data.customer?.passport || '—' }),
    tp('misc_pdf.pinfl', { v: data.customer?.pinfl || '—' }),
  ];
  customerLines.forEach(line => {
    doc.text(line, 14, y);
    y += 5;
  });
  y += 4;

  // Contract summary
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(tp('misc_pdf.terms'), 14, y);
  y += 6;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  const contractLines = [
    tp('misc_pdf.total', { v: formatUZS(data.contract.total_amount) }),
    tp('misc_pdf.monthly', { v: formatUZS(data.contract.monthly_payment) }),
    tp('misc_pdf.term', { n: data.contract.term_months }),
  ];
  contractLines.forEach(line => {
    doc.text(line, 14, y);
    y += 5;
  });
  y += 4;

  // Sale items table
  if (data.saleItems.length > 0) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(tp('misc_pdf.items'), 14, y);
    y += 2;

    autoTable(doc, {
      startY: y,
      head: [['#', tp('misc_pdf.th_product'), tp('misc_pdf.th_qty'), tp('misc_pdf.th_unit'), tp('misc_pdf.th_price'), tp('misc_pdf.th_total')]],
      body: data.saleItems.map((item, i) => [
        String(i + 1),
        item.product_name,
        String(item.quantity),
        item.unit,
        formatUZS(item.unit_price),
        formatUZS(item.total),
      ]),
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [41, 65, 148], textColor: 255 },
      margin: { left: 14, right: 14 },
    });

    y = (doc as any).lastAutoTable.finalY + 8;
  }

  // Payment schedule
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(tp('misc_pdf.schedule'), 14, y);
  y += 2;

  autoTable(doc, {
    startY: y,
    head: [['#', tp('misc_pdf.th_date'), tp('misc_pdf.th_payment'), tp('misc_pdf.th_remaining')]],
    body: data.schedule.map(row => [
      String(row.n),
      new Date(row.due_date).toLocaleDateString(pdfLng() === 'en' ? 'en-US' : 'uz', { day: '2-digit', month: '2-digit', year: 'numeric' }),
      formatUZS(row.amount),
      formatUZS(row.remaining),
    ]),
    styles: { fontSize: 9, cellPadding: 2 },
    headStyles: { fillColor: [41, 65, 148], textColor: 255 },
    margin: { left: 14, right: 14 },
  });

  y = (doc as any).lastAutoTable.finalY + 15;

  // Check if signatures fit, otherwise add page
  if (y > 260) {
    doc.addPage();
    y = 30;
  }

  // Signatures
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(tp('misc_pdf.seller'), 14, y);
  doc.text(tp('misc_pdf.buyer'), pageWidth / 2 + 10, y);
  y += 15;
  doc.setFont('helvetica', 'normal');
  doc.line(14, y, 85, y);
  doc.line(pageWidth / 2 + 10, y, pageWidth - 14, y);
  y += 5;
  doc.setFontSize(8);
  doc.text(tp('misc_pdf.signature'), 45, y);
  doc.text(tp('misc_pdf.signature'), pageWidth / 2 + 45, y);

  // Open in new tab
  const pdfBlob = doc.output('blob');
  const url = URL.createObjectURL(pdfBlob);
  window.open(url, '_blank');
}
